import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Search, Loader2, KanbanSquare, ListChecks, Building2, Layers } from "lucide-react";

export function ConsoleSearch({ onOpenTask, onOpenProject }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState({ tasks: [], projects: [] });
  const inputRef = useRef(null);
  const reqId = useRef(0);

  // Cmd/Ctrl+K to open.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) { setQ(""); setRes({ tasks: [], projects: [] }); return; }
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes({ tasks: [], projects: [] }); setLoading(false); return; }
    setLoading(true);
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/console/search?q=${encodeURIComponent(term)}`);
        if (id === reqId.current) setRes({ tasks: data.tasks || [], projects: data.projects || [] });
      } catch (e) { if (id === reqId.current) setRes({ tasks: [], projects: [] }); }
      finally { if (id === reqId.current) setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const pickTask = (t) => { setOpen(false); onOpenTask?.(t); };
  const pickProject = (p) => { setOpen(false); onOpenProject?.(p); };
  const hasResults = res.tasks.length > 0 || res.projects.length > 0;
  const termLong = q.trim().length >= 2;

  return (
    <>
      <button data-testid="console-search-btn" onClick={() => setOpen(true)} title="Search tasks & projects (⌘K)"
        className="relative flex items-center justify-center h-9 w-9 rounded-full border border-[#EEEFEF] bg-white hover:bg-[#F6F6FD] transition-colors">
        <Search className="h-[18px] w-[18px] text-[#595D62]" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-xl p-0 gap-0 top-[12%] translate-y-0" data-testid="console-search-dialog">
          <DialogTitle className="sr-only">Search tasks and projects</DialogTitle>
          <div className="flex items-center gap-2.5 px-4 border-b border-[#f0f0f3]">
            {loading ? <Loader2 className="h-4.5 w-4.5 text-[#c4c5cc] animate-spin shrink-0" /> : <Search className="h-4.5 w-4.5 text-[#9FA1A4] shrink-0" />}
            <input ref={inputRef} data-testid="console-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setOpen(false); } }}
              placeholder="Search tasks & projects…"
              className="flex-1 h-14 bg-transparent outline-none text-[15px] text-[#06060C] placeholder:text-[#c4c5cc]" />
            <kbd className="hidden sm:block text-[10px] font-mono-data text-[#c4c5cc] border border-[#EEEFEF] rounded px-1.5 py-0.5">ESC</kbd>
          </div>

          <div className="max-h-[60vh] overflow-y-auto py-2">
            {!termLong ? (
              <p className="text-sm text-[#9FA1A4] text-center py-10 px-4">Start typing to search across all tasks and projects.</p>
            ) : !hasResults && !loading ? (
              <p data-testid="console-search-empty" className="text-sm text-[#9FA1A4] text-center py-10 px-4">No tasks or projects match “{q.trim()}”.</p>
            ) : (
              <>
                {res.projects.length > 0 && (
                  <div className="px-2 pb-1">
                    <p className="px-2 pt-1.5 pb-1 text-[10px] font-mono-data uppercase tracking-wider text-[#9FA1A4] flex items-center gap-1.5"><KanbanSquare className="h-3 w-3" /> Projects</p>
                    {res.projects.map((p) => (
                      <button key={p.id} data-testid="console-search-project" onClick={() => pickProject(p)}
                        className="w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-[#F6F6FD] transition-colors">
                        <span className="h-8 w-8 rounded-lg bg-[#EDE7FF] text-[#5b2fd6] flex items-center justify-center shrink-0">{p.is_internal ? <Layers className="h-4 w-4" /> : <KanbanSquare className="h-4 w-4" />}</span>
                        <span className="min-w-0 flex-1">
                          <span className="text-sm text-[#06060C] truncate block">{p.name || "Untitled project"}</span>
                          <span className="text-[11px] text-[#9FA1A4] truncate block">{p.code ? `${p.code} · ` : ""}{p.is_internal ? "Internal" : (p.client || "Client")}</span>
                        </span>
                        {p.status && <span className="text-[10px] font-mono-data uppercase tracking-wider text-[#595D62] bg-[#F6F6FD] border border-[#EEEFEF] rounded-full px-2 py-0.5 shrink-0">{p.status}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {res.tasks.length > 0 && (
                  <div className="px-2 pb-1">
                    <p className="px-2 pt-1.5 pb-1 text-[10px] font-mono-data uppercase tracking-wider text-[#9FA1A4] flex items-center gap-1.5"><ListChecks className="h-3 w-3" /> Tasks</p>
                    {res.tasks.map((t) => (
                      <button key={t.id} data-testid="console-search-task" onClick={() => pickTask(t)}
                        className="w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-[#F6F6FD] transition-colors">
                        <span className="h-8 w-8 rounded-lg bg-[#F6F6FD] text-[#595D62] flex items-center justify-center shrink-0"><ListChecks className="h-4 w-4" /></span>
                        <span className="min-w-0 flex-1">
                          <span className="text-sm text-[#06060C] truncate block">{t.title || "Untitled task"}</span>
                          <span className="text-[11px] text-[#9FA1A4] truncate block flex items-center gap-1">{t.code ? `${t.code} · ` : ""}{t.project_name || "—"}{t.client && <span className="inline-flex items-center gap-0.5"> · <Building2 className="h-3 w-3" /> {t.is_internal ? "Internal" : t.client}</span>}</span>
                        </span>
                        {t.status && <span className="text-[10px] font-mono-data uppercase tracking-wider text-[#595D62] bg-[#F6F6FD] border border-[#EEEFEF] rounded-full px-2 py-0.5 shrink-0">{t.status}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
